// src/layouts/CitizenLayout.tsx - UPDATED VERSION
import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  MapPin, 
  Users,
  User,
  Settings,
  Bell,
  Menu,
  X,
  LogOut,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Define the Citizen type locally if not defined elsewhere
interface Citizen {
  id: number;
  phoneNumber: string;
  fullName?: string;
  email?: string;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
  [key: string]: any; // Allow additional properties
}

const CitizenLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { citizen, logout } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', path: '/citizen/dashboard', icon: <LayoutDashboard size={20} /> },
    { name: 'My Reports', path: '/citizen/reports', icon: <BarChart3 size={20} /> },
    { name: 'Report Issue', path: '/citizen/report/new', icon: <FileText size={20} /> },
    { name: 'Map View', path: '/citizen/map', icon: <MapPin size={20} /> },
    { name: 'Community', path: '/citizen/community', icon: <Users size={20} /> },
    { name: 'Profile', path: '/citizen/profile', icon: <User size={20} /> },
    { name: 'Settings', path: '/citizen/settings', icon: <Settings size={20} /> },
  ];

  // Count unread notifications (mock data)
  const unreadNotifications = 3;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Helper function to safely get ward name
  const getWardName = (citizen: Citizen | null | undefined): string => {
    if (!citizen) return 'Not set';
    return citizen.wardName || citizen.ward || 'Not set';
  };

  // Helper function to safely get citizen's first name
  const getFirstName = (citizen: Citizen | null | undefined): string => {
    if (!citizen?.fullName) return 'Citizen';
    return citizen.fullName.split(' ')[0];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg">
            <div className="p-4">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-gray-900">Citizen Portal</h2>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={24} />
                </button>
              </div>
              <nav className="space-y-2">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                    onClick={() => setSidebarOpen(false)}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64">
        <div className="flex flex-col w-64 bg-white border-r border-gray-200">
          <div className="p-6">
            <h1 className="text-xl font-bold text-gray-900">Citizen Portal</h1>
            <p className="text-sm text-gray-600 mt-1">Community Engagement Platform</p>
          </div>
          
          <nav className="flex-1 px-4 space-y-2">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                {item.icon}
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="text-blue-600" size={20} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 truncate">{citizen?.fullName || 'Citizen'}</p>
                <p className="text-sm text-gray-500 truncate">{citizen?.phoneNumber || 'No phone'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Menu size={24} />
                </button>
                <div className="ml-4 lg:ml-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Welcome back, {getFirstName(citizen as Citizen | null)}
                  </h2>
                  <p className="text-sm text-gray-500 hidden sm:block">
                    {location.pathname === '/citizen/dashboard' && 'Track your reports and updates'}
                    {location.pathname === '/citizen/reports' && 'View and manage your submitted reports'}
                    {location.pathname === '/citizen/report/new' && 'Submit a new community issue report'}
                    {location.pathname === '/citizen/map' && 'View reports on interactive map'}
                    {location.pathname === '/citizen/community' && 'See campaigns and updates from your ward aspirants'}
                    {location.pathname === '/citizen/profile' && 'Manage your account information'}
                    {location.pathname === '/citizen/settings' && 'Configure your preferences'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <Bell size={20} />
                  {unreadNotifications > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
                
                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                      {citizen?.fullName || 'Citizen'}
                    </p>
                    <p className="text-xs text-gray-500">Citizen</p>
                  </div>
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="text-blue-600" size={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="lg:ml-64 border-t border-gray-200 bg-white py-4">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
              <p>© {new Date().getFullYear()} Citizen Connect. All rights reserved.</p>
              <div className="flex items-center gap-4 mt-2 md:mt-0">
                <span className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${
                    getWardName(citizen as Citizen | null) !== 'Not set' ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></span>
                  {getWardName(citizen as Citizen | null) !== 'Not set' 
                    ? `Ward: ${getWardName(citizen as Citizen | null)}` 
                    : 'Ward not set'}
                </span>
                <span>•</span>
                <button 
                  onClick={() => window.location.reload()}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CitizenLayout;